import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Menu,
  Modal,
  Radio,
  Select,
  Skeleton,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconArrowDown,
  IconArrowLeft,
  IconArrowsMove,
  IconArrowUp,
  IconDots,
  IconFolder,
  IconFolderPlus,
  IconPencil,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import classes from './Documents.module.css';
import {
  type ApplyMode,
  applyTarget,
  FOLDER_SCREEN_PATH,
  type FolderActionKind,
  type FolderBadge,
  type FolderFormState,
  type FolderRow,
  type FolderSection,
  focusAfterReorder,
  folderBadges,
  folderFormSchema,
  folderOperation,
  folderRows,
  impactRows,
  initialFolderValues,
  localFolderLabel,
  moveTargets,
  removableFolder,
} from './folder-tree.js';
import type { DocumentSource, DocumentVessel } from './source.js';
import type {
  FolderTreeImpact,
  FolderTreeNode,
  FolderTreeOperation,
  FolderTreeOutcome,
  FolderTreeView,
} from './types.js';

/**
 * Klasör ağacı yönetimi (ADR 0037 §11; ADR 0038 §8, §9; plan M8a4 Task 16): web ofiste ISM Forms
 * ▸ Form Categories ve Manuals ▸ "Manage folders". Gemi seçicili: geminin ağacı ya da şirket
 * aynası (her zaman salt okunur). İşlemler ekle, yeniden adlandır, sırala, taşı, emekliye ayır ve
 * geri al; her biri "Apply to" ile bu gemiye, seçili gemilere ya da bütün gemilere uygulanır. Bir
 * gemide hata varsa hiçbir gemi değişmez. Yetki `document_tree.edit` ("documents.tree");
 * yetkisizde ekran salt okunurdur. Silme yoktur [NR496 4.3.2a].
 */

/** Şirket aynası seçici değeri (geminin kimliği yerine). */
export const COMPANY_LIBRARY = 'company';

const BADGE_TONE: Record<FolderBadge, StatusTone> = {
  template: 'neutral',
  officeModified: 'info',
  retired: 'warning',
};

const SECTION_PATH: Record<FolderSection, string> = {
  ism: '/compliance/ism-forms',
  manuals: '/compliance/manuals',
};

type ControlName = 'up' | 'down' | 'menu' | 'restore';

export interface FolderTreeEditorProps {
  section: FolderSection;
  view: FolderTreeView;
  vessels: readonly DocumentVessel[];
  /** Seçili kütüphane: geminin kimliği ya da `COMPANY_LIBRARY`. */
  selected: string;
  onSelect: (value: string) => void;
  /** Kişi bu ağacı düzenleyebilir mi (yetki ve gemi ağacı). */
  canEdit: boolean;
  onAction: (kind: FolderActionKind, node: FolderTreeNode | null) => void;
  /** Satır denetimlerinin kaydı: sıralamadan sonra odak düğümde kalır. */
  controlRef?: (key: string, control: ControlName, element: HTMLElement | null) => void;
}

function Badges({ node }: { node: FolderTreeNode }) {
  const { t } = useTranslation();
  return (
    <span className={classes.ftBadges}>
      {folderBadges(node).map((badge) => (
        <span key={badge} title={t(`documents.folders.badgeHints.${badge}`)}>
          <StatusBadge tone={BADGE_TONE[badge]} variant="pill">
            {t(`documents.folders.badges.${badge}`)}
          </StatusBadge>
        </span>
      ))}
    </span>
  );
}

/** Ağaç düzenleyici: gemi seçici, ağaç, rozetler, belge sayıları ve satır denetimleri. */
export function FolderTreeEditor({
  section,
  view,
  vessels,
  selected,
  onSelect,
  canEdit,
  onAction,
  controlRef,
}: FolderTreeEditorProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.folders.${key}`, options ?? {});
  const language = i18n.language ?? 'en';
  const rows = useMemo(() => folderRows(view.nodes), [view.nodes]);
  const company = view.library === 'company';
  const editable = canEdit && !company;
  const label = (node: FolderTreeNode) => localFolderLabel(node, language);
  const register = (key: string | null, control: ControlName) =>
    controlRef && key !== null
      ? (element: HTMLElement | null) => controlRef(key, control, element)
      : undefined;

  const children = (list: readonly FolderRow[], parentId: string | null, level: number) =>
    list.filter((row) =>
      level === 1 ? row.level === 1 : row.node.parentId === parentId && row.level === level,
    );

  const renderRows = (
    list: readonly FolderRow[],
    parentId: string | null,
    level: number,
    retired: boolean,
  ): ReactNode => {
    const items = children(list, parentId, level);
    if (items.length === 0) return null;
    return (
      <ul
        className={classes.ftList}
        data-level={level}
        aria-label={
          level === 1
            ? retired
              ? k('retiredCaption')
              : `${k('caption')} – ${t(`documents.sections.${section}.title`)}`
            : undefined
        }
      >
        {items.map((row) => {
          const { node } = row;
          const text = label(node);
          return (
            <li
              key={node.id}
              className={classes.ftItem}
              data-node-key={node.key ?? undefined}
              data-retired={retired || undefined}
            >
              <div className={classes.ftRow} style={{ paddingInlineStart: 8 + (level - 1) * 22 }}>
                <IconFolder
                  size={18}
                  stroke={1.7}
                  className={retired ? classes.ftRetiredIcon : classes.treeFolderIcon}
                  aria-hidden
                />
                <span className={classes.ftLabel} title={text}>
                  {text}
                </span>
                <Badges node={node} />
                <span className={classes.catCount}>
                  {k('documents', { count: node.documentCount })}
                </span>
                {editable && node.key !== null ? (
                  <span className={classes.ftActions}>
                    {retired ? (
                      level === 1 ? (
                        <Button
                          size="compact-xs"
                          variant="light"
                          leftSection={<IconArrowBackUp size={14} />}
                          aria-label={k('aria.restore', { name: text })}
                          ref={register(node.key, 'restore')}
                          onClick={() => onAction('restore', node)}
                        >
                          {k('actions.restore')}
                        </Button>
                      ) : null
                    ) : (
                      <>
                        <ActionIcon
                          variant="subtle"
                          size="md"
                          aria-label={k('aria.moveUp', { name: text })}
                          disabled={row.first}
                          ref={register(node.key, 'up')}
                          onClick={() => onAction('up', node)}
                        >
                          <IconArrowUp size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          size="md"
                          aria-label={k('aria.moveDown', { name: text })}
                          disabled={row.last}
                          ref={register(node.key, 'down')}
                          onClick={() => onAction('down', node)}
                        >
                          <IconArrowDown size={16} />
                        </ActionIcon>
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              size="md"
                              aria-label={k('aria.menu', { name: text })}
                              ref={register(node.key, 'menu')}
                            >
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconFolderPlus size={15} />}
                              onClick={() => onAction('addChild', node)}
                            >
                              {k('actions.addChild')}
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconPencil size={15} />}
                              onClick={() => onAction('rename', node)}
                            >
                              {k('actions.rename')}
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconArrowsMove size={15} />}
                              onClick={() => onAction('move', node)}
                            >
                              {k('actions.move')}
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              color="red"
                              leftSection={<IconArchive size={15} />}
                              onClick={() => onAction('retire', node)}
                            >
                              {k('actions.retire')}
                            </Menu.Item>
                            {removableFolder(view, node) ? (
                              <Menu.Item
                                color="red"
                                leftSection={<IconTrash size={15} />}
                                onClick={() => onAction('remove', node)}
                              >
                                {k('actions.remove')}
                              </Menu.Item>
                            ) : null}
                          </Menu.Dropdown>
                        </Menu>
                      </>
                    )}
                  </span>
                ) : null}
              </div>
              {renderRows(list, node.id, level + 1, retired)}
            </li>
          );
        })}
      </ul>
    );
  };

  const options = [
    ...vessels.map((v) => ({ value: v.id, label: v.name })),
    { value: COMPANY_LIBRARY, label: k('company') },
  ];
  let meta: string;
  if (company) meta = k('companyHint');
  else if (view.template?.name && view.template.version !== null) {
    meta = k('template', { name: view.template.name, version: view.template.version });
  } else meta = k('noTemplate');

  return (
    <Panel
      title={k('caption')}
      className={classes.catPanel}
      extra={
        editable ? (
          <Button
            size="xs"
            leftSection={<IconFolderPlus size={15} />}
            onClick={() => onAction('add', null)}
          >
            {k('actions.add')}
          </Button>
        ) : undefined
      }
    >
      <div className={classes.ftToolbar}>
        <Select
          className={classes.ftSelector}
          size="sm"
          label={k('library')}
          data={options}
          value={selected}
          allowDeselect={false}
          onChange={(value) => value && onSelect(value)}
          comboboxProps={{ withinPortal: true }}
        />
        <Text size="xs" c="dimmed" className={classes.ftMeta}>
          {meta}
        </Text>
      </div>
      {!editable && !company ? (
        <Text size="xs" c="dimmed" className={classes.catHint}>
          {k('readOnly')}
        </Text>
      ) : null}
      {rows.live.length === 0 && rows.retired.length === 0 ? (
        <EmptyState title={k('empty')} />
      ) : (
        renderRows(rows.live, null, 1, false)
      )}
      {rows.retired.length > 0 ? (
        <section className={classes.ftRetired} aria-label={k('retiredCaption')}>
          <h3 className={classes.ftRetiredTitle}>
            <IconArchive size={16} aria-hidden /> {k('retiredCaption')}
          </h3>
          <Text size="xs" c="dimmed" className={classes.catHint}>
            {k('retiredHint')}
          </Text>
          {renderRows(rows.retired, null, 1, true)}
        </section>
      ) : null}
    </Panel>
  );
}

// --- İşlem formu ("Apply to") ---

export interface FolderOperationFormProps {
  kind: FolderActionKind;
  section: FolderSection;
  node: FolderTreeNode | null;
  view: FolderTreeView;
  vessels: readonly DocumentVessel[];
  /** Ekrandaki gemi ("This vessel"). */
  current: DocumentVessel;
  /** Emekliye ayırmada gemi başına belge sayısı; `null` yükleniyor. */
  impact?: readonly FolderTreeImpact[] | null | undefined;
  busy?: boolean;
  onSubmit: (operation: FolderTreeOperation, target: 'all' | string[]) => void;
  onCancel: () => void;
}

/**
 * Her işlem "Apply to" sorar (ADR 0037 §11): bu gemi, seçili gemiler ya da bütün gemiler.
 * Emekliye ayırma onayı gemi başına belge sayısını ve "Retired folders" notunu gösterir.
 */
export function FolderOperationForm({
  kind,
  section,
  node,
  view,
  vessels,
  current,
  impact,
  busy = false,
  onSubmit,
  onCancel,
}: FolderOperationFormProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.folders.${key}`, options ?? {});
  const language = i18n.language ?? 'en';
  const form = useForm<FolderFormState>({
    resolver: zodResolver(folderFormSchema(kind, section, node)),
    defaultValues: { ...initialFolderValues(kind, node), mode: 'this', vessels: [current.id] },
  });
  const mode = form.watch('mode');
  const chosen = form.watch('vessels');
  const errors = form.formState.errors;
  const message = (key: string | undefined) => (key ? k(`form.${key}`) : undefined);
  const named = kind === 'add' || kind === 'addChild' || kind === 'rename';
  const target = applyTarget(mode, current.id, chosen);
  const shown = impact ? impactRows(impact, target) : null;

  return (
    <form
      className={classes.ftForm}
      noValidate
      onSubmit={form.handleSubmit((value) =>
        onSubmit(
          folderOperation(kind, section, node, value),
          applyTarget(value.mode, current.id, value.vessels),
        ),
      )}
    >
      {named ? (
        <>
          <TextInput
            label={k('form.code')}
            required
            maxLength={32}
            error={message(errors.code?.message)}
            {...form.register('code')}
          />
          <TextInput
            label={k('form.nameEn')}
            required
            maxLength={120}
            error={message(errors.nameEn?.message)}
            {...form.register('nameEn')}
          />
          <TextInput
            label={k('form.nameTr')}
            description={k('form.nameTrHint')}
            maxLength={120}
            error={message(errors.nameTr?.message)}
            {...form.register('nameTr')}
          />
        </>
      ) : null}
      {kind === 'move' && node ? (
        <Controller
          control={form.control}
          name="parentKey"
          render={({ field }) => (
            <Select
              label={k('form.parent')}
              allowDeselect={false}
              data={moveTargets(view.nodes, node).map((target) => ({
                value: target?.key ?? '',
                label: target ? localFolderLabel(target, language) : k('form.root'),
              }))}
              value={field.value}
              onChange={(value) => field.onChange(value ?? '')}
              comboboxProps={{ withinPortal: true }}
            />
          )}
        />
      ) : null}
      {kind === 'retire' ? (
        <Alert color="info" icon={<IconArchive size={16} />} className={classes.ftNote}>
          {k('form.retireNote')}
        </Alert>
      ) : null}
      {kind === 'remove' ? (
        <Alert color="red" icon={<IconTrash size={16} />} className={classes.ftNote}>
          {k('form.removeNote')}
        </Alert>
      ) : null}
      {kind === 'restore' ? (
        <Text size="sm" className={classes.ftNote}>
          {k('form.restoreNote')}
        </Text>
      ) : null}

      <Controller
        control={form.control}
        name="mode"
        render={({ field }) => (
          <Radio.Group
            label={k('form.applyTo')}
            value={field.value}
            onChange={(value) => field.onChange(value as ApplyMode)}
          >
            <div className={classes.ftModes}>
              <Radio value="this" label={k('form.thisVessel', { name: current.name })} />
              <Radio value="selected" label={k('form.selectedVessels')} />
              <Radio value="all" label={k('form.allVessels')} />
            </div>
          </Radio.Group>
        )}
      />
      {mode === 'selected' ? (
        <Controller
          control={form.control}
          name="vessels"
          render={({ field }) => (
            <Checkbox.Group
              label={k('form.vessels')}
              value={field.value}
              onChange={field.onChange}
              error={message(errors.vessels?.message)}
            >
              <div className={classes.ftVessels}>
                {vessels.map((vessel) => (
                  <Checkbox key={vessel.id} value={vessel.id} label={vessel.name} />
                ))}
              </div>
            </Checkbox.Group>
          )}
        />
      ) : null}
      {node?.key ? (
        <Text size="xs" c="dimmed">
          {k('form.keyNote')}
        </Text>
      ) : null}

      {kind === 'retire' ? (
        shown === null ? (
          <Text size="xs" c="dimmed" aria-busy="true">
            {k('form.impactLoading')}
          </Text>
        ) : (
          <table className={classes.ftTable}>
            <thead>
              <tr>
                <th scope="col" className={classes.ftHead}>
                  {k('form.impactVessel')}
                </th>
                <th scope="col" className={classes.ftHead}>
                  {k('form.impactDocuments')}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.vesselId}>
                  <td className={classes.ftCell} data-absent={!row.present || undefined}>
                    {row.name}
                  </td>
                  <td className={classes.ftCell} data-absent={!row.present || undefined}>
                    {!row.present
                      ? k('form.impactAbsent')
                      : row.retired
                        ? k('form.impactRetired')
                        : k('documents', { count: row.documents })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}

      <div className={classes.ftFormActions}>
        <Button variant="default" size="sm" onClick={onCancel} disabled={busy}>
          {k('form.cancel')}
        </Button>
        <Button
          type="submit"
          size="sm"
          {...(kind === 'retire' || kind === 'remove' ? { color: 'red' } : {})}
          loading={busy}
        >
          {k('form.apply')}
        </Button>
      </div>
    </form>
  );
}

// --- Sonuç ---

/** Gemi başına sonuç: uygulandı, değişiklik yok, gemide yok ya da reddedildi (kodla). */
export function FolderOperationResult({
  outcome,
  onClose,
}: {
  outcome: FolderTreeOutcome;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.folders.${key}`, options ?? {});
  const applied = outcome.vessels.filter((v) => v.status === 'applied').length;
  const tone: Record<string, StatusTone> = {
    applied: 'success',
    unchanged: 'neutral',
    not_present: 'neutral',
    error: 'danger',
  };
  return (
    <div className={classes.ftForm}>
      <Alert
        color={outcome.ok ? 'green' : 'red'}
        icon={outcome.ok ? undefined : <IconAlertTriangle size={16} />}
      >
        {outcome.ok ? k('result.done', { count: applied }) : k('result.rejected')}
      </Alert>
      <table className={classes.ftTable}>
        <thead>
          <tr>
            <th scope="col" className={classes.ftHead}>
              {k('result.vessel')}
            </th>
            <th scope="col" className={classes.ftHead}>
              {k('result.status')}
            </th>
          </tr>
        </thead>
        <tbody>
          {outcome.vessels.map((vessel) => (
            <tr key={vessel.vesselId}>
              <td className={classes.ftCell}>{vessel.name ?? vessel.vesselId}</td>
              <td className={classes.ftCell}>
                <StatusBadge tone={tone[vessel.status] ?? 'neutral'}>
                  {k(`result.${vessel.status}`)}
                </StatusBadge>
                {vessel.code ? (
                  <Text size="xs" c="dimmed" component="span" className={classes.ftCode}>
                    {t(`documents.errors.${vessel.code}`, {
                      defaultValue: t('documents.errors.GENERIC'),
                    })}
                  </Text>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={classes.ftFormActions}>
        <Button size="sm" onClick={onClose}>
          {k('result.close')}
        </Button>
      </div>
    </div>
  );
}

// --- Ekran ---

type Dialog =
  | { kind: FolderActionKind; node: FolderTreeNode | null }
  | { outcome: FolderTreeOutcome };

function selectedFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get('vessel');
  return value && value.trim() !== '' ? value : null;
}

/**
 * Form Categories (`section="ism"`) ve Manuals ▸ Manage folders (`section="manuals"`): yalnız web
 * ofis. Seçili gemi adreste `?vessel=` olarak durur (`company`: şirket aynası).
 */
export function FolderTreeScreen({
  section,
  source,
}: {
  section: FolderSection;
  source: DocumentSource;
}) {
  const { t, i18n } = useTranslation();
  const shell = useShell();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.folders.${key}`, options ?? {});
  const office = source.office;
  const folders = office?.folders;
  const [vessels, setVessels] = useState<DocumentVessel[] | null>(null);
  const [view, setView] = useState<FolderTreeView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [impact, setImpact] = useState<FolderTreeImpact[] | null>(null);
  const [busy, setBusy] = useState(false);
  const controls = useRef(new Map<string, HTMLElement>());
  const pendingFocus = useRef<{ key: string; control: ControlName } | null>(null);

  const fromUrl = selectedFromSearch(shell?.search ?? '');
  const selected = fromUrl ?? vessels?.[0]?.id ?? COMPANY_LIBRARY;
  const current = vessels?.find((v) => v.id === selected) ?? null;

  useEffect(() => {
    let live = true;
    void source.permissions().then(
      (permissions) => live && setCanEdit(Boolean(permissions.tree)),
      () => live && setCanEdit(false),
    );
    return () => {
      live = false;
    };
  }, [source]);

  useEffect(() => {
    if (!office) return;
    let live = true;
    office.vessels().then(
      (list) => live && setVessels(list),
      (failure) => live && setError(errorCode(failure)),
    );
    return () => {
      live = false;
    };
  }, [office]);

  const load = useCallback(async () => {
    if (!folders || vessels === null) return;
    setError(null);
    try {
      const next = await folders.tree(
        selected === COMPANY_LIBRARY ? { library: 'company' } : { vesselId: selected },
        section,
      );
      setView(next);
    } catch (failure) {
      setError(errorCode(failure));
    }
  }, [folders, vessels, selected, section]);

  useEffect(() => {
    setView(null);
    void load();
  }, [load]);

  // Pencere kapanınca odak geri döner (Modal'ın kendi dönüşü kapalı): sıralamadan sonra taşınan
  // düğümün düğmesine (yeni yerinde; uca vardıysa öteki yön), yoksa pencereyi açan denetime
  // (klavye; WAI-ARIA "Dialog")
  const trigger = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (dialog !== null) return;
    const wanted = pendingFocus.current;
    pendingFocus.current = null;
    const target = wanted
      ? controls.current.get(`${wanted.key}:${wanted.control}`)
      : trigger.current;
    trigger.current = null;
    if (!target) return;
    const frame = requestAnimationFrame(() => target.focus());
    return () => cancelAnimationFrame(frame);
  }, [dialog]);

  const select = (value: string) => {
    const path = FOLDER_SCREEN_PATH[section];
    shell?.navigate(`${path}?vessel=${encodeURIComponent(value)}`);
  };

  const open = (kind: FolderActionKind, node: FolderTreeNode | null) => {
    // Menü öğesi menüyle kapanır: odak satırın menü düğmesine döner
    const active = typeof document === 'undefined' ? null : document.activeElement;
    const fromMenu = active instanceof HTMLElement && active.closest('[role="menu"]') !== null;
    trigger.current = fromMenu
      ? (controls.current.get(`${node?.key ?? ''}:menu`) ?? null)
      : active instanceof HTMLElement
        ? active
        : null;
    setNotice(null);
    setImpact(null);
    setDialog({ kind, node });
    if (kind === 'retire' && node?.key && folders) {
      folders.impact(section, node.key).then(setImpact, () => setImpact([]));
    }
  };

  const submit = async (operation: FolderTreeOperation, target: 'all' | string[]) => {
    if (!folders || !view || dialog === null || !('kind' in dialog)) return;
    const node = dialog.node;
    setBusy(true);
    try {
      const outcome = await folders.apply({
        operation,
        vessels: target,
        ...(node && view.vesselId
          ? { expected: { vesselId: view.vesselId, rowVersion: node.rowVersion } }
          : {}),
      });
      if (operation.type === 'reorder' && outcome.ok && node?.key) {
        // Odak taşınan düğümde kalır: yeni yerinde aynı yöndeki düğme (uca vardıysa öteki yön)
        pendingFocus.current = focusAfterReorder(
          folderRows(view.nodes).live,
          node.key,
          operation.direction,
        );
      }
      setDialog({ outcome });
      await load();
    } catch (failure) {
      setDialog(null);
      setNotice(errorCode(failure));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const sectionTitle = t(`documents.sections.${section}.title`);
  const title = section === 'ism' ? k('title') : k('manualsTitle');
  let body: ReactNode;
  if (!office || !folders) {
    body = null;
  } else if (error) {
    body = (
      <Panel>
        <EmptyState
          tone="danger"
          title={t('documents.screen.error')}
          description={t(`documents.errors.${error}`, { defaultValue: '' })}
          action={
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => void load()}
            >
              {t('documents.screen.retry')}
            </Button>
          }
        />
      </Panel>
    );
  } else if (!view || vessels === null) {
    body = (
      <Panel title={k('caption')}>
        <div aria-busy="true" className={classes.catSkeleton}>
          <span className={classes.srOnly}>{t('documents.screen.loading')}</span>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={22} width={i % 2 ? '58%' : '72%'} />
          ))}
        </div>
      </Panel>
    );
  } else {
    body = (
      <FolderTreeEditor
        section={section}
        view={view}
        vessels={vessels}
        selected={selected}
        onSelect={select}
        canEdit={canEdit && view.editable}
        onAction={open}
        controlRef={(key, control, element) => {
          const id = `${key}:${control}`;
          if (element) controls.current.set(id, element);
          else controls.current.delete(id);
        }}
      />
    );
  }

  const formDialog = dialog && 'kind' in dialog ? dialog : null;
  let formTitle = '';
  if (formDialog) {
    formTitle = formDialog.node
      ? k(`form.${formDialog.kind}`, {
          name: localFolderLabel(formDialog.node, i18n.language ?? 'en'),
        })
      : k('form.add');
  }

  return (
    <div className={classes.catScreen}>
      <PageHeader
        title={title}
        {...(section === 'manuals' ? { crumb: k('manualsCrumb') } : {})}
        description={section === 'ism' ? k('subtitle') : k('manualsSubtitle')}
        actions={
          section === 'manuals' ? (
            <Button
              variant="default"
              size="xs"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => shell?.navigate(SECTION_PATH[section])}
            >
              {k('back', { section: sectionTitle })}
            </Button>
          ) : undefined
        }
      />
      {notice ? (
        <Alert
          color="red"
          icon={<IconAlertTriangle size={16} />}
          withCloseButton
          closeButtonLabel={t('documents.screen.dismiss')}
          onClose={() => setNotice(null)}
          className={classes.notice}
        >
          {t(`documents.errors.${notice}`, { defaultValue: t('documents.errors.GENERIC') })}
        </Alert>
      ) : null}
      {vessels !== null && vessels.length === 0 ? (
        <Text size="sm" c="dimmed">
          {k('noVessels')}
        </Text>
      ) : null}
      {body}
      <Modal
        opened={dialog !== null}
        returnFocus={false}
        onClose={() => (busy ? undefined : setDialog(null))}
        title={dialog && 'outcome' in dialog ? k('result.status') : formTitle}
        closeButtonProps={{ 'aria-label': t('documents.screen.dismiss') }}
        size="lg"
        centered
      >
        {formDialog && view && current ? (
          <FolderOperationForm
            kind={formDialog.kind}
            section={section}
            node={formDialog.node}
            view={view}
            vessels={vessels ?? []}
            current={current}
            impact={formDialog.kind === 'retire' ? impact : undefined}
            busy={busy}
            onSubmit={(operation, target) => void submit(operation, target)}
            onCancel={() => setDialog(null)}
          />
        ) : null}
        {dialog && 'outcome' in dialog ? (
          <FolderOperationResult outcome={dialog.outcome} onClose={() => setDialog(null)} />
        ) : null}
      </Modal>
    </div>
  );
}
